import { useEffect, useState } from "react";
import { Loader2, Truck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, apiError } from "@/lib/api";
import { formatDate, formatIsoDay } from "@/lib/format";
import { StatusBadge } from "@/lib/orders";
import { Field } from "@/lib/finance";
import { EmptyRow, LoadingRow, PageHeader } from "@/components/common/States";
import { useAuth } from "@/context/AuthContext";

const FIELDS = [["vehicle_plate", "Araç / Plaka"], ["driver_name", "Şoför"], ["carrier", "Kargo Firması"], ["tracking_no", "Takip No"]];

function ShippingDialog({ o, onClose, onDone }) {
  const [f, setF] = useState({ vehicle_plate: "", driver_name: "", carrier: "", tracking_no: "", ship_date: "", note: "", ...o.shipping, package_count: o.shipping?.package_count || o.package_count || 0 });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put(`/sevkiyat/${o.id}`, { ...f, package_count: Number(f.package_count) || 0 }); toast.success("Sevk bilgisi kaydedildi"); onDone(); } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="shipping-dialog">
        <DialogHeader><DialogTitle>Sevk Bilgisi · {o.order_no}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          {FIELDS.map(([k, l]) => <Field key={k} label={l}><Input value={f[k] || ""} onChange={set(k)} data-testid={`shipping-${k}`} /></Field>)}
          <Field label="Koli Sayısı"><Input type="number" min={0} value={f.package_count} onChange={set("package_count")} data-testid="shipping-package_count" /></Field>
          <Field label="Sevk Tarihi"><Input type="date" value={f.ship_date || ""} onChange={set("ship_date")} data-testid="shipping-ship_date" /></Field>
          <Field label="Not" className="col-span-2"><Input value={f.note || ""} onChange={set("note")} data-testid="shipping-note" /></Field>
          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>Vazgeç</Button>
            <Button type="submit" disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="shipping-save">{busy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Kaydet</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Sevkiyat() {
  const { can } = useAuth();
  const [tab, setTab] = useState("hazir");
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [edit, setEdit] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => { setRes(null); const t = setTimeout(() => api.get("/sevkiyat", { params: { tab, q } }).then(({ data }) => setRes(data)), 200); return () => clearTimeout(t); }, [tab, q, reload]);
  return (
    <div data-testid="sevkiyat-page">
      <PageHeader title="Sevkiyat" subtitle="Sevke hazır ve son 30 günde faturalaşan siparişlerin araç, şoför ve kargo bilgileri" testid="sevkiyat-title" />
      <div className="flex flex-wrap gap-3 items-center mb-3">
        <Tabs value={tab} onValueChange={setTab}><TabsList>
          <TabsTrigger value="hazir" data-testid="sevkiyat-tab-hazir">Sevke Hazır <span className="ml-1 text-[10px] font-mono text-slate-400">{res?.counts.hazir}</span></TabsTrigger>
          <TabsTrigger value="faturalasti" data-testid="sevkiyat-tab-faturalasti">Faturalaşan (30 gün) <span className="ml-1 text-[10px] font-mono text-slate-400">{res?.counts.faturalasti}</span></TabsTrigger>
        </TabsList></Tabs>
        <Input className="h-9 w-64" placeholder="Sipariş, fatura, plaka, takip no..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="sevkiyat-search" />
      </div>
      <div className="panel overflow-x-auto">
        <table className="erp-table" data-testid="sevkiyat-table">
          <thead><tr><th>Sipariş</th><th>Fatura</th><th>Cari</th><th className="text-right">Koli</th><th>Araç / Şoför</th><th>Kargo / Takip</th><th>Sevk Tarihi</th><th>Durum</th><th /></tr></thead>
          <tbody>
            {!res ? <LoadingRow cols={9} /> : res.items.length === 0 ? <EmptyRow cols={9} text="Kayıt yok" /> : res.items.map((o) => (
              <tr key={o.id} data-testid={`sevkiyat-row-${o.order_no}`}>
                <td className="font-mono text-xs font-semibold">{o.order_no}</td>
                <td className="font-mono text-xs">{o.invoice_no || "—"}{o.invoiced_at && <div className="text-[10px] text-slate-400">{formatDate(o.invoiced_at)}</div>}</td>
                <td className="max-w-[220px] truncate">{o.customer_title}</td>
                <td className="text-right font-mono">{o.shipping.package_count || o.package_count || "—"}</td>
                <td className="text-xs">{o.shipping.vehicle_plate || "—"}<div className="text-slate-400">{o.shipping.driver_name}</div></td>
                <td className="text-xs">{o.shipping.carrier || "—"}<div className="font-mono text-slate-400">{o.shipping.tracking_no}</div></td>
                <td className="font-mono text-xs">{formatIsoDay(o.shipping.ship_date)}</td>
                <td><StatusBadge status={o.status} /></td>
                <td className="text-right">{can("sevkiyat", "edit") && <Button size="sm" variant="outline" className="h-7" onClick={() => setEdit(o)} data-testid={`sevkiyat-edit-${o.order_no}`}><Truck className="h-3.5 w-3.5 mr-1" />Sevk Bilgisi</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && <ShippingDialog o={edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); setReload((r) => r + 1); }} />}
    </div>
  );
}
